import React, {
  useEffect, useMemo, useRef,
} from "react";
import Scrollbar from "react-scrollbars-custom";
import { Button, Empty } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import AnnotateBoxActionsPanel from "./AnnotateBoxActionsPanel";
import "./AnnotateBoxPointsListPanel.css";

const formatCoords = (box) => {
  const x1 = Math.round(box.x);
  const y1 = Math.round(box.y);
  const x2 = Math.round(box.x + box.width);
  const y2 = Math.round(box.y + box.height);
  return `[${x1}, ${y1}, ${x2}, ${y2}]`;
};

const AnnotateBoxPointsListPanel = ({
  boxes,
  colorOptions,
  isEditMode,
  selectedBox,
  selectedId,
  readOnly = false,
  onAddBox,
  onSelectLabel,
  onSelectBox,
  onDeleteBox,
}) => {
  const rowRefs = useRef(new Map());

  const rows = useMemo(() => {
    const safeBoxes = Array.isArray(boxes) ? boxes : [];
    const safeOptions = Array.isArray(colorOptions) ? colorOptions : [];

    return safeBoxes.map((box) => {
      const optionMatch = safeOptions.find((opt) => opt.value === box.customLabel)
        || safeOptions.find((opt) => opt.color === box.customColor);
      const title = optionMatch?.displayName ?? optionMatch?.label ?? "Box";
      const color = optionMatch?.color ?? box.customColor ?? "#000000";

      return {
        id: box.id,
        title,
        color,
        subtitle: formatCoords(box),
      };
    });
  }, [boxes, colorOptions]);

  useEffect(() => {
    if (!selectedId) return;
    const el = rowRefs.current.get(selectedId);
    if (!el) return;
    // Use "center" to keep selected row in the middle; change to "start" for top align.
    el.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [selectedId, rows.length]);

  return (
    <div className={`AnnotateImageModal__PointsList ${readOnly ? "AnnotateImageModal__PointsList--readonly" : ""}`}>
      <AnnotateBoxActionsPanel
        title="Boxes"
        colorOptions={colorOptions}
        isEditMode={isEditMode}
        selectedBox={selectedBox}
        selectedId={selectedId}
        readOnly={readOnly}
        onAddBox={onAddBox}
        onSelectLabel={onSelectLabel}
      />
      <div className="AnnotateImageModal__PointsListBody">
        <Scrollbar
          className="AnnotateImageModal__PointsScrollbar"
          disableTrackYWidthCompensation
          disableTracksWidthCompensation
        >
          {rows.length === 0 ? (
            <div className="AnnotateImageModal__PointsEmpty">
              <Empty description="No Data" />
            </div>
          ) : (
            <div className="AnnotateImageModal__PointsItems">
              {rows.map((row) => (
                <div
                  key={row.id}
                  ref={(node) => {
                    if (!node) {
                      rowRefs.current.delete(row.id);
                      return;
                    }
                    rowRefs.current.set(row.id, node);
                  }}
                  role={readOnly ? undefined : "button"}
                  tabIndex={readOnly ? undefined : 0}
                  className={`AnnotateImageModal__PointItem ${selectedId === row.id ? "AnnotateImageModal__PointItem--selected" : ""}`}
                  onClick={() => {
                    if (readOnly) return;
                    onSelectBox(row.id);
                  }}
                  onKeyDown={(e) => {
                    if (readOnly) return;
                    if (e.key === "Enter" || e.key === " ") onSelectBox(row.id);
                  }}
                >
                  <div className="AnnotateImageModal__PointText">
                    <div className="AnnotateImageModal__PointTitle">
                      <span className="AnnotateImageModal__ColorDot" style={{ backgroundColor: row.color }} />
                      <span className="AnnotateImageModal__PointTitleText">{row.title}</span>
                    </div>
                    <div className="AnnotateImageModal__PointSubtitle">{row.subtitle}</div>
                  </div>
                  <Button
                    danger
                    size="small"
                    className="AnnotateImageModal__PointDeleteBtn"
                    icon={<DeleteOutlined />}
                    disabled={readOnly}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (readOnly) return;
                      onDeleteBox(row.id);
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </Scrollbar>
      </div>
    </div>
  );
};

export default AnnotateBoxPointsListPanel;
